import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import { toast } from 'react-toastify';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import { websiteSettingsAPI } from '../../services/api';

const RAW_API_URL = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api').replace(/\/+$/, '');
const API_ORIGIN = RAW_API_URL.endsWith('/api') ? RAW_API_URL.slice(0, -4) : RAW_API_URL;

const buildMediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const cleaned = path.replace(/^\/+/, '').replace(/^storage\//, '').replace(/^public\//, '');
  return `${API_ORIGIN}/storage/${cleaned}`;
};

const backgroundFade = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const progressAnimation = keyframes`
  0% { transform: translateX(-60%); }
  50% { transform: translateX(-5%); }
  100% { transform: translateX(60%); }
`;

const LoginContainer = styled.div`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: ${props => {
    if (props.$mode === 'image' && props.$backgroundImage) {
      return `linear-gradient(135deg, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.4)), url(${props.$backgroundImage}) center/cover no-repeat`;
    }
    return `linear-gradient(135deg, ${props.$gradientStart} 0%, ${props.$gradientEnd} 100%)`;
  }};
  transition: background 0.6s ease;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 460px;
`;

const LoginCard = styled(Card)`
  width: 100%;
  padding: 2.5rem;
  border-radius: ${props => props.theme.borderRadius['2xl']};
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(12px);
`;

const BackgroundSelector = styled.div`
  margin-top: 1.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
`;

const BackgroundThumb = styled.button`
  width: 46px;
  height: 46px;
  border-radius: ${props => props.theme.borderRadius.full};
  border: 2px solid ${props => props.$active ? props.theme.colors.primary : 'rgba(255, 255, 255, 0.4)'};
  background-image: ${props => props.$image ? `url(${props.$image})` : 'none'};
  background-color: ${props => props.$image ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  background-size: cover;
  background-position: center;
  padding: 0;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);

  &:hover {
    transform: translateY(-2px);
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ProgressWrapper = styled.div`
  margin-top: 1.25rem;
  width: 100%;
  background: ${props => props.$trackColor};
  border-radius: ${props => props.$style === 'thin' ? '999px' : props.theme.borderRadius.full};
  height: ${props => {
    if (props.$style === 'flat') return '10px';
    if (props.$style === 'thin') return '4px';
    return '14px';
  }};
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 50%;
  background: ${props => props.$color};
  border-radius: inherit;
  animation: ${progressAnimation} 1.2s ease-in-out infinite;
`;

const LoadingLabel = styled.p`
  margin: 0.75rem 0 0;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  text-align: center;
`;

const LogoSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const LogoIcon = styled.div`
  width: 120px;
  height: 120px;
  min-width: 120px;
  min-height: 120px;
  background-color: ${props => props.hasLogo ? 'transparent' : props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.white};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  margin: 0 auto 1rem auto;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
  
  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PasswordInputContainer = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: ${props => props.theme.borderRadius.sm};
  
  &:hover {
    color: ${props => props.theme.colors.text.primary};
  }
`;

const LoginButton = styled(Button)`
  width: 100%;
  margin-top: 1rem;
`;

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [websiteSettings, setWebsiteSettings] = useState(null);
  const [activeBackgroundIndex, setActiveBackgroundIndex] = useState(0);
  const [pauseRotation, setPauseRotation] = useState(false);
  
  const { theme } = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchWebsiteSettings();
    
    // Listen for settings updates (from admin panel)
    const handleSettingsUpdate = () => {
      fetchWebsiteSettings();
    };
    
    window.addEventListener('website-settings-updated', handleSettingsUpdate);
    
    // Poll for settings updates every 30 seconds (in case user doesn't refresh)
    const pollInterval = setInterval(() => {
      fetchWebsiteSettings();
    }, 30000);
    
    return () => {
      window.removeEventListener('website-settings-updated', handleSettingsUpdate);
      clearInterval(pollInterval);
    };
  }, []);
  
  const fetchWebsiteSettings = async () => {
    try {
      const response = await websiteSettingsAPI.getSettings();
      console.log('Login page: Raw response:', response);
      console.log('Login page: Response data:', response.data);
      
      // Handle different response structures
      const settings = response.data?.settings || response.data;
      
      if (settings) {
        console.log('Login page: Settings to apply:', {
          mode: settings.login_background_mode,
          primaryColor: settings.primary_color,
          progressColor: settings.login_progress_color,
          progressStyle: settings.login_progress_style,
          backgroundsCount: settings.login_background_gallery?.length || 0,
          allSettings: settings,
        });
        setWebsiteSettings(settings);
      } else {
        console.warn('Login page: No settings found in response');
      }
    } catch (error) {
      console.error('Error fetching website settings:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };
  
  const activeBackgroundUrlFromSettings = websiteSettings?.login_active_background_url || null;
  const normalizedBackgrounds = useMemo(() => {
    const galleryItems = Array.isArray(websiteSettings?.login_background_gallery)
      ? websiteSettings.login_background_gallery
      : [];
    
    const normalized = galleryItems
      .map(item => ({
        path: item?.path || null,
        url: item?.url || buildMediaUrl(item?.path),
      }))
      .filter(item => item.url);

    if (!normalized.length && Array.isArray(websiteSettings?.login_background_images)) {
      return websiteSettings.login_background_images
        .map(path => ({
          path,
          url: buildMediaUrl(path),
        }))
        .filter(item => item.url);
    }

    if (!normalized.length && activeBackgroundUrlFromSettings) {
      return [{
        path: websiteSettings?.login_active_background || null,
        url: activeBackgroundUrlFromSettings,
      }];
    }

    return normalized;
  }, [websiteSettings, activeBackgroundUrlFromSettings]);
  
  const backgroundUrls = normalizedBackgrounds.map(item => item.url);
  
  useEffect(() => {
    if (!backgroundUrls.length) {
      setActiveBackgroundIndex(0);
      return;
    }
  
    if (activeBackgroundUrlFromSettings) {
      const activeIdx = backgroundUrls.indexOf(activeBackgroundUrlFromSettings);
      if (activeIdx !== -1) {
        setActiveBackgroundIndex(activeIdx);
        return;
      }
    }
  
    setActiveBackgroundIndex(prev => prev % backgroundUrls.length);
  }, [backgroundUrls, activeBackgroundUrlFromSettings]);
  
  useEffect(() => {
    if (!pauseRotation) return undefined;
    const resumeTimer = setTimeout(() => setPauseRotation(false), 15000);
    return () => clearTimeout(resumeTimer);
  }, [pauseRotation]);
  
  const backgroundMode = websiteSettings?.login_background_mode || (backgroundUrls.length ? 'image' : 'gradient');
  const activeBackgroundImage = backgroundMode === 'image' && backgroundUrls.length
    ? backgroundUrls[activeBackgroundIndex]
    : null;
  const gradientStart = websiteSettings?.primary_dark_color || theme.colors.primaryDark || '#6B21A8';
  const gradientEnd = websiteSettings?.primary_color || theme.colors.primary || '#9333EA';
  
  // Use white for progress color if not set, otherwise use the setting or theme primary
  const progressColor = websiteSettings?.login_progress_color || '#ffffff';
  const progressTrackColor = websiteSettings?.login_progress_track_color || 'rgba(255,255,255,0.35)';
  const progressStyle = websiteSettings?.login_progress_style || 'pill';
  const showBackgroundSelector = backgroundMode === 'image' && backgroundUrls.length > 1;
  
  console.log('Login page: Active settings:', {
    backgroundMode,
    activeBackgroundImage,
    gradientStart,
    gradientEnd,
    progressColor,
    progressTrackColor,
    progressStyle,
    showBackgroundSelector,
  });
  
  useEffect(() => {
    if (
      backgroundMode !== 'image' ||
      backgroundUrls.length <= 1 ||
      pauseRotation
    ) {
      return undefined;
    }
    
    const rotation = setInterval(() => {
      setActiveBackgroundIndex(prev => (prev + 1) % backgroundUrls.length);
    }, 8000);
    
    return () => clearInterval(rotation);
  }, [backgroundMode, backgroundUrls, pauseRotation]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await login(formData);
      if (result.success) {
        toast.success('Login successful!');
        navigate('/app/dashboard');
      } else {
        toast.error(result.error || 'Login failed');
      }
    } catch (error) {
      toast.error('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBackgroundSelect = (index) => {
    setActiveBackgroundIndex(index);
    setPauseRotation(true);
  };
  
  return (
    <LoginContainer
      theme={theme}
      $mode={backgroundMode}
      $backgroundImage={activeBackgroundImage}
      $gradientStart={gradientStart}
      $gradientEnd={gradientEnd}
    >
      <ContentWrapper>
        <LoginCard>
          <LogoSection>
            <LogoIcon theme={theme} hasLogo={!!websiteSettings?.logo_path}>
              {websiteSettings?.logo_path ? (
                <img 
                  src={websiteSettings.logo_path} 
                  alt={websiteSettings.website_name || "EWHRDN Logo"}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextElementSibling) {
                      e.target.nextElementSibling.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <span style={{ display: websiteSettings?.logo_path ? 'none' : 'flex' }}>E</span>
            </LogoIcon>
            <Title theme={theme}>{websiteSettings?.website_name || 'EWHRDN'}</Title>
            <Subtitle theme={theme}>
              Ethiopian Women Human Rights Defenders Network
            </Subtitle>
          </LogoSection>
          
          <Form onSubmit={handleSubmit}>
            <Input
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder="Enter your username"
              required
            />
            
            <PasswordInputContainer>
              <Input
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                required
              />
              <PasswordToggle
                theme={theme}
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </PasswordToggle>
            </PasswordInputContainer>
            
            <LoginButton
              type="submit"
              variant="primary"
              loading={loading}
              leftIcon={<LogIn size={16} />}
            >
              Sign In
            </LoginButton>

            {loading && (
              <>
                <ProgressWrapper
                  theme={theme}
                  $trackColor={progressTrackColor}
                  $style={progressStyle}
                >
                  <ProgressFill $color={progressColor} />
                </ProgressWrapper>
                <LoadingLabel theme={theme}>
                  Securing your workspace...
                </LoadingLabel>
              </>
            )}
          </Form>
        </LoginCard>

        {showBackgroundSelector && (
          <BackgroundSelector>
            {backgroundUrls.map((url, index) => (
              <BackgroundThumb
                key={`${url}-${index}`}
                $image={url}
                $active={index === activeBackgroundIndex}
                onClick={() => handleBackgroundSelect(index)}
                aria-label={`Switch to background ${index + 1}`}
                title="Switch background"
                theme={theme}
              />
            ))}
          </BackgroundSelector>
        )}
      </ContentWrapper>
      
    </LoginContainer>
  );
};

export default Login;
